import { Input, Select, Card, Button, Modal } from "antd";
import { User, Heart, Phone, Mail, Rotate3D, RotateCcwIcon } from 'lucide-react';
import AddressAutocomplete from "../AddressAutocomplete";
import { toast } from "react-toastify";
import axios from "axios";
import { API_BASE_URL } from "../../api";
import { useEffect } from "react";
import { useState } from "react";

import DoctorSelect from "../Dropdowns/DoctorSelect";
import AgentSelect from "../Dropdowns/AgentSelect";
import PaymentTypeSelect from "../Dropdowns/PaymentTypeSelect";

const { Option } = Select;

const PatientAndAppointmentForm = ({
  commonData,
  appointmentData,
  loadingDoctors,
  loadingAgents,
  handleCommonDataChange,
  setCommonData,
  setAppointmentData,
  setSelectedPatientId,

  paymentType,
  setPaymentType,
}) => {
  const [numberChecked, setNumberChecked] = useState(false);
  const [patientList, setPatientList] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isExternalDoctor, setIsExternalDoctor] = useState(false);

  const hasSelectedDoctor = Boolean(commonData.doctor);
  const hasSelectedAgent = Boolean(commonData.agent);

  const disabledPaymentTypes = [
    ...(hasSelectedDoctor ? [] : ["referred"]),
    ...(hasSelectedAgent ? [] : ["ctb"]),
  ];

  useEffect(() => {
    if (!paymentType) return;
    if (disabledPaymentTypes.includes(paymentType.toLowerCase())) {
      setPaymentType("");
    }
  }, [paymentType, disabledPaymentTypes, setPaymentType]);

  useEffect(() => {
    if (!appointmentData.date) {
      setAppointmentData(prev => ({ ...prev, date: new Date().toISOString().split('T')[0] }));
    }
  }, [appointmentData.date, setAppointmentData]);

  const checkNumber = async () => {
    const phone = commonData.mobile;
    if (phone && phone.length >= 8 && phone.length <= 13) {
      setIsLoadingPatients(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/patients?mobile=${phone}&depth=2`);
        const patient = response.data.patients[0];

        if (!patient) {
          toast.info("No existing patient found. Please enter new patient details.", {
            position: "top-right",
            autoClose: 2000,
          });
          setNumberChecked(true);
          return;
        }

        if (patient.status === 'inactive') {
          toast.error("This patient profile has been disabled by an administrator.", {
            position: "top-right",
            autoClose: 3000,
          });
          setNumberChecked(false);
          setShowPatientDropdown(false);
          return;
        }

        setPatientList(response.data.patients);
        setShowPatientDropdown(true);

      } catch (error) {
        toast.error("Error fetching patient data.", {
          position: "top-right",
          autoClose: 3000,
        });
        console.error("Error fetching patient data:", error);
      } finally {
        setIsLoadingPatients(false);
      }
    } else {
      toast.error("Please enter a valid phone number.", {
        position: "top-right",
        autoClose: 1500,
      });
    }
  };

  return (
    <>
      <Card className="mb-6 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Patient Information</h3>
        </div>

        <div className="flex p-2 w-full justify-between items-center mb-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-5 items-center justify-start">
            <p className="block text-md mb-2 font-bold text-nowrap text-center">Contact Number *</p>
            <Input
              prefix={<Phone className="w-4 h-4 text-gray-400" />}
              placeholder="Phone number"
              name="mobile"
              value={commonData.mobile}
              onChange={handleCommonDataChange}
              required
              className="border-gray-200 focus:ring-blue-500"
            />
          </div>
          <div>
            <Button type="primary" className="ml-4 px-3" onClick={checkNumber}> {isLoadingPatients ? "Checking..." : "Check Number"}</Button>
            <Button type="default" className="ml-4 px-3"
              onClick={() => {
                setNumberChecked(true);
                setSelectedPatientId(null);
                setCommonData(prev => ({
                  ...prev,
                  patientName: '',
                  age: '',
                  gender: '',
                  address: '',
                  email: '',
                }));
              }}>New Patient</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Patient Name *</label>
            <Input
              prefix={<User className="w-4 h-4 text-gray-400" />}
              placeholder="Full name"
              name="patientName"
              value={commonData.patientName}
              onChange={handleCommonDataChange}
              required
              className="border-gray-200 focus:ring-blue-500"
              disabled={!numberChecked}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Age *</label>
            <Input
              type="number"
              placeholder="Age"
              name="age"
              value={commonData.age}
              onChange={handleCommonDataChange}
              required
              min="0"
              max="150"
              className="border-gray-200 focus:ring-blue-500"
              disabled={!numberChecked}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
            <Select
              name="gender"
              value={commonData.gender}
              onChange={(value) => setCommonData(prev => ({ ...prev, gender: value }))}
              required
              className="w-full"
              placeholder="Select Gender"
              disabled={!numberChecked}
            >
              <Option value="">Select Gender</Option>
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <AddressAutocomplete
              value={commonData.address}
              onChange={(value) => setCommonData(prev => ({ ...prev, address: value }))}
              placeholder="Start typing your address in Bangladesh..."
              disabled={!numberChecked}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <Input
              prefix={<Mail className="w-4 h-4 text-gray-400" />}
              type="email"
              placeholder="abc@gmail.com"
              name="email"
              value={commonData.email}
              onChange={handleCommonDataChange}
              className="border-gray-200 focus:ring-blue-500"
              disabled={!numberChecked}
            />
          </div>
        </div>
      </Card>

      <Card className="mb-6 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Medical Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="flex justify-between">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                Doctor Name 
              </label>
              <button 
                  onClick={(e)=>{
                    e.preventDefault();
                    if ( isExternalDoctor ){
                      setCommonData({
                        ...commonData,
                        externalDoctorName: null,
                      })
                    } else {
                      setCommonData({
                        ...commonData,
                        doctor: null
                      })
                    }
                    setIsExternalDoctor(!isExternalDoctor);
                  }}
                  className="text-blue-800 underline hover:font-bold hover:text-blue-500 ml-2 rounded-lg px-1.5 py-0 border border-1 border-blue-800 flex gap-2 items-center"> 
                    <RotateCcwIcon className="h-3 w-3"/>  { isExternalDoctor ?  "Internal" : "External" } 
                </button>
              </div>
            { !isExternalDoctor ? <DoctorSelect
              selectedId={commonData.doctor}
              onSelectID={(doctor) => {
                setCommonData(prev => ({ ...prev, doctor: doctor }));
              }}
              onChange={(name) => {
                setCommonData(prev => ({ ...prev, doctorName: name }))
              }}
              placeholder={loadingDoctors ? "Loading doctors..." : "Select Doctor"}
              className="w-full"
            /> :
            <Input
              placeholder="Enter External Doctor Name"
              name="doctorName"
              value={commonData.externalDoctorName}
              onChange={(e)=>{
                setCommonData({
                  ...commonData,
                  externalDoctorName: e.target.value
                })
              }}
            />
            }
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name</label>
            <AgentSelect
              selectedId={commonData.agent}
              onSelectID={(agent) => setCommonData(prev => ({ ...prev, agent: agent }))}
              placeholder={loadingAgents ? "Loading agents..." : "Select Agent"}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
            <PaymentTypeSelect
              value={paymentType}
              onChange={(type) => setPaymentType(type)}
              disabledItems={disabledPaymentTypes}
              placeholder="Select Payment Type"
              className="w-full"
            />
          </div>
        </div>
      </Card>

      <Modal
        title="Select Existing Patient"
        open={showPatientDropdown}
        onCancel={() => setShowPatientDropdown(false)}
        footer={null}
      >
        <div>
          <div className="flex justify-between items-center mb-4">
            <p> Create New Patient </p>
            <Button color="default" variant="outlined" className="mt-2" onClick={() => {
              setShowPatientDropdown(false);
              setNumberChecked(true);
              setSelectedPatientId(null);
              setCommonData(prev => ({
                ...prev,
                patientName: '',
                age: '',
                gender: '',
                address: '',
                email: '',
              }));
            }}>
              New Patient
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select an existing patient: </label>
          {patientList && patientList.length > 0 ? (patientList.map((patient) => (
            <div key={patient._id} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-semibold">{patient.patientName || 'N/A'} (Age: {patient.age || 'N/A'}, Gender: {patient.gender || 'N/A'})</p>
                <p className="text-sm text-gray-600">Mobile: {patient.mobile || 'N/A'}</p>
                <p className="text-sm text-gray-600">Address: {patient.address || 'N/A'}</p>
              </div>
              <div>
                <Button type="primary" onClick={() => {
                  setCommonData(prev => ({
                    ...prev,
                    patientName: patient.patientName,
                    age: patient.age,
                    gender: patient.gender,
                    mobile: patient.mobile,
                    address: patient.address,
                  }));
                  setSelectedPatientId(patient._id);
                  setShowPatientDropdown(false);
                }}>
                  Select
                </Button>
              </div>
            </div>
          ))) : (
            <p>No patients found with this number.</p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default PatientAndAppointmentForm;
